import { observer } from "mobx-react-lite";
import { useState, useEffect } from "react";
import { matchPath } from "react-router";
import { useLocation } from "react-router-dom";
import { HeadsUpSidebar } from "@/components/heads-up";
import { MemItSidePanelView } from "@/components/mem-it";
import styled from "@emotion/styled";
import { motion } from "motion/react";
import { useAppStore } from "@/store";
import { SidePanelRouteType, SidePanelTab } from "@/store/routing/types";
import { SIDE_PANEL_COLLAPSED_HEIGHT_PX, OPEN_TOP_GAP_PX } from "@/app/router/constants";
import { ZIndex } from "@/domains/design/constants";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { appRoutes } from "@/app/router/appRoutes";
import { SmartViewChatPanel } from "@/smart-views/components/SmartViewChatPanel";

export const SidePanelRouter = observer(function SidePanelRouter() {
  const { pageStore, store } = useAppStore();
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const location = useLocation();

  const memItStore = pageStore.memItPage;
  const sidePanelStore = store.sidePanel;
  const smartViewIdMatch = matchPath(
    appRoutes.smartViewsView({ params: { smartViewId: ":smartViewId" }, starPattern: true }).path,
    location.pathname
  );
  const smartViewId = smartViewIdMatch?.params.smartViewId;
  const isSmartViewChat = Boolean(smartViewId);
  const storeSessionId = memItStore.session?.id;
  const activeRoute = sidePanelStore.activeRoute;
  const activeTabId = sidePanelStore.activeTabId;
  const isChatOpen = activeTabId === SidePanelTab.MEM_IT;
  const openChatTop = smartViewId ? 0 : OPEN_TOP_GAP_PX;
  const routeSessionId =
    activeRoute.type === SidePanelRouteType.MEM_IT ? activeRoute.sessionId : undefined;

  useEffect(() => {
    // Track side panel view
    if (store.onboarding.isActive) return;

    trackEvent(TrackedEvent.SidePanelView);
  }, [store.onboarding.isActive]);

  useEffect(() => {
    if (isSmartViewChat) return;

    // Prefer the side panel route's sessionId when available
    if (routeSessionId) {
      setSessionId(routeSessionId);
      return;
    }

    // Fall back to the current session in the store if available
    if (storeSessionId) {
      setSessionId(storeSessionId);
      return;
    }

    // Try to resume an active session
    // Otherwise create or get a placeholder session id
    memItStore
      .resumeActiveSession()
      .then(resumedSessionId => {
        if (resumedSessionId) {
          setSessionId(resumedSessionId);
          return;
        }
        // No active session found, get a placeholder session id
        return memItStore.getPlaceholderSessionId();
      })
      .then(id => {
        if (id) {
          setSessionId(id);
        }
      })
      .catch(error => {
        logger.error({
          message: "Error getting session id in SidePanelRouter",
          info: {
            error: objectModule.safeErrorAsJson(error as Error),
          },
        });
      });
  }, [isSmartViewChat, memItStore, routeSessionId, storeSessionId]);

  // Reset animation skip flag after first render
  useEffect(() => {
    if (sidePanelStore.shouldSkipAnimation && sidePanelStore.isInitialized) {
      // Reset the flag animation skip once we render
      const timeoutId = setTimeout(() => {
        sidePanelStore.resetShouldSkipAnimation();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [sidePanelStore.isInitialized, sidePanelStore.shouldSkipAnimation, sidePanelStore]);

  const openSmartViewChat = () => {
    sidePanelStore.switchTab(SidePanelTab.MEM_IT);
    sidePanelStore.open();
  };

  return (
    <Container>
      {sidePanelStore.isInitialized && !smartViewId && (
        <HeadsUpContainer>
          <HeadsUpSidebar />
        </HeadsUpContainer>
      )}

      <MemItOverlay
        initial={false}
        animate={{
          top: isChatOpen ? openChatTop : `calc(100% - ${SIDE_PANEL_COLLAPSED_HEIGHT_PX}px)`,
        }}
        transition={
          sidePanelStore.shouldSkipAnimation
            ? { duration: 0 }
            : { type: "spring", bounce: 1, mass: 0.17, stiffness: 200 }
        }
      >
        <MemItContainer
          onClick={() => {
            if (!isChatOpen) {
              if (smartViewId) {
                openSmartViewChat();
                return;
              }

              sidePanelStore.goToMemIt(sessionId);
            }
          }}
        >
          {smartViewId ? (
            <SmartViewChatPanel
              smartViewId={smartViewId}
              isClosed={!isChatOpen}
              onOpen={openSmartViewChat}
            />
          ) : (
            <MemItSidePanelView
              sessionId={sessionId}
              isClosed={!isChatOpen}
              onClose={sidePanelStore.goToHeadsUp}
            />
          )}
        </MemItContainer>
      </MemItOverlay>
    </Container>
  );
});

const Container = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const HeadsUpContainer = styled.div`
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  z-index: ${ZIndex.Zero};
`;

const MemItOverlay = styled(motion.div)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${ZIndex.SidebarMemItOverlay};
  display: flex;
  flex-direction: column;
  will-change: transform;
`;

const MemItContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;
